import { useMutation } from "@tanstack/react-query";
import { collection, doc, writeBatch } from "firebase/firestore";
import { db } from "../../../config/firebase";
import { splitArrayIntoBatches } from "../../../utils/arrayUtils";

export function useBatchUpsertCalendarDates({ concurrency = 3 } = {}) {
  return useMutation({
    mutationFn: async (params) => {
      const { data, calendarId } = params || {};

      if (!calendarId) {
        throw new Error("calendarId is required.");
      }

      if (!Array.isArray(data)) {
        throw new Error("data must be an array.");
      }

      const batches = splitArrayIntoBatches(data);
      const datesRef = collection(db, "calendars", calendarId, "dates");

      const commitFns = batches.map((itemBatch) => async () => {
        const batch = writeBatch(db);

        for (const item of itemBatch) {
          const docRef = doc(datesRef, item.id);
          batch.set(docRef, item);
        }

        await batch.commit();
        return itemBatch.length;
      });

      const results = [];
      const queue = commitFns.slice();
      const workerCount = Math.min(concurrency, commitFns.length);

      const workers = new Array(workerCount).fill(null).map(async () => {
        while (true) {
          const next = queue.shift();
          if (!next) {
            break;
          }

          try {
            const count = await next();
            results.push({ success: true, count });
          } catch (error) {
            results.push({ success: false, error });
          }
        }
      });

      await Promise.all(workers);

      const successCount = results
        .filter((result) => result.success)
        .reduce((sum, result) => sum + (result.count || 0), 0);
      const failedBatches = results.filter((result) => !result.success);

      const summary = {
        success: failedBatches.length === 0,
        totalCount: data.length,
        successCount,
        failedBatches,
      };

      if (failedBatches.length) {
        const error = new Error(`Failed to complete the following number of batches: ${failedBatches.length}`);
        error.data = summary;
        throw error;
      }

      return summary;
    },
    onError: (err) => {
      console.error("Failed to create calendar dates:", err);
    },
  });
}
